import { getPermissionMatrix, savePermissionMatrix } from '../api/settings';
import { hasUsableSession } from '../utils/authHelpers';

/**
 * 권한 표(matrix) — "어떤 역할이 어떤 기능을 볼 수 있는가"의 단일 출처.
 *
 * 지금까지는 이 판단이 usePermission.js 안에 `!isClient && !isDeliveryRole` 같은 식으로
 * 흩어져 있어서, 규칙 하나 바꾸려면 코드를 고쳐야 했다. 여기로 모아두면 화면
 * (「권한 관리」 탭)에서 체크박스로 켜고 끌 수 있다.
 *
 * 바꾼 값은 **서버에 저장**된다(`/api/settings/permissions`, MASTER 만 저장 — 2026-10-02).
 * 예전에는 저장한 브라우저에만 남아 다른 계정 화면은 전혀 바뀌지 않았다. 이제 모든 계정이
 * 서버 값을 읽는다 — 로그인 직후, 화면을 옮길 때(1분에 한 번 이내), 창으로 돌아올 때.
 * ⚠️ 프론트는 화면을 가릴 뿐이다. 실제 차단은 서버에서 같이 해야 한다.
 */

/** 권한 표의 열(役割). DELIVERY_THAILAND/VIETNAM 은 규칙이 같아 DELIVERY 하나로 묶는다.
    Manager 라는 이름은 쓰지 않는다 — 둘로 갈라 AE 와 KOL 이 됐다(2026-08-12). */
export const MATRIX_ROLES = [
  { key: 'MASTER', label: 'MASTER', desc: '대표' },
  { key: 'AE', label: 'AE', desc: 'BLB 내부 — 금액·정산 담당' },
  { key: 'KOL', label: 'KOL Specialist', desc: 'BLB 내부 — 섭외·운영 담당' },
  { key: 'CLIENT', label: 'Client', desc: '광고주' },
  { key: 'DELIVERY', label: 'Delivery', desc: '배송사 (태국·베트남)' },
  { key: 'INFLUENCER', label: 'Influencer', desc: '크리에이터' },
];

/**
 * 권한 표의 행(기능). group 은 화면에서 묶어 보여주는 용도.
 * key 는 usePermission() 이 돌려주는 이름과 1:1 이다.
 *
 * where : 이 권한이 실제로 화면 어디를 가리는지 (2026-07-29 코드에서 확인한 위치)
 * what  : 끄면 무엇이 사라지는지
 * unused: 코드 어디에서도 아직 안 쓰이는 권한 — 켜고 꺼도 화면이 바뀌지 않는다
 * 설명 옆 사진은 public/assets/permission-help/<key>.png 를 넣으면 자동으로 뜬다.
 */
export const PERMISSION_DEFS = [
  // 메뉴 접근
  {
    key: 'canViewDashboard', route: '/dashboard', group: '메뉴', label: '대시보드',
    where: '왼쪽 사이드바 「대시보드」 · 주소 /dashboard',
    what: '끄면 메뉴가 사라지고, 주소로 직접 들어가도 본인 기본 화면으로 튕깁니다.',
  },
  {
    key: 'canViewKolsDB', route: '/kols-db', group: '메뉴', label: 'KOLs DB',
    where: '왼쪽 사이드바 「KOLs DB」 · 주소 /kols-db',
    what: '전체 크리에이터 DB 화면. 끄면 메뉴·주소 접근 모두 막힙니다.',
  },
  {
    key: 'canViewMyKols', route: '/my-kols', group: '메뉴', label: 'My KOLs',
    where: '왼쪽 사이드바 「My KOLs」 · 주소 /my-kols',
    what: '캠페인에 배정한 크리에이터 목록. 끄면 메뉴·주소 접근 모두 막힙니다.',
  },
  {
    key: 'canViewCampaign', route: '/campaign', group: '메뉴', label: '캠페인',
    where: '왼쪽 사이드바 「캠페인」 · 캠페인 목록(2차 사이드바) · 주소 /campaign',
    what: '캠페인 목록과 상세 전체. 끄면 캠페인 업무 자체가 안 보입니다.',
  },
  {
    key: 'canViewShipping', route: '/shipping', group: '메뉴', label: '배송(Shipping)',
    where: '왼쪽 사이드바 「Shipping」 · 주소 /shipping',
    what: '배송 목록·상세. 배송사는 자기 국가 건만 보입니다(국가 제한은 별도).',
  },
  // Delivery 메뉴는 쓰지 않기로 해서 표에서 뺐다(2026-07-29 대표님 결정).
  // 권한 자체(canViewDelivery)는 라우트 보호에 아직 쓰이므로 DEFAULT_MATRIX 에는 남겨 둔다.
  {
    key: 'canViewInvoice', route: '/invoices', group: '메뉴', label: '청구(인보이스)',
    where: '왼쪽 사이드바 「청구」 · 주소 /invoices · 캠페인 상세 [KOL Settlement] 탭의 내보내기(↗)',
    what: '클라이언트에게 보내는 청구서를 만들고 목록으로 봅니다. 우리 원가와 마진이 드러나므로 클라이언트·배송사는 꺼두세요.',
  },
  {
    key: 'canViewSettlement', route: '/settlement', group: '메뉴', label: '정산',
    where: '왼쪽 사이드바 「정산」 · 캠페인 상세의 [정산] 탭 · 대시보드 정산 요약',
    what: '받을 돈·줄 돈·마진이 모두 드러나는 화면입니다. 금액을 못 보는 역할은 꺼두세요.',
  },
  {
    key: 'canManageClientInvites', route: '/client-invites', group: '메뉴', label: '회사 초대코드 관리',
    where: '왼쪽 사이드바 맨 아래 「회사·권한 관리」 · 지금 보고 있는 이 화면',
    what: '초대코드 발급·수정과 이 권한 표. 끄면 본인도 못 들어오니 주의하세요.',
    danger: true,
  },
  {
    key: 'canViewTrash', route: '/trash', group: '메뉴', label: '휴지통',
    where: '왼쪽 사이드바 「휴지통」 · 주소 /trash',
    what: '지운 캠페인·배송·요금 유형을 보고 되살리고, 삭제 예약을 취소합니다. 누가 무엇을 지웠는지 드러나므로 우리 쪽 사람만 켜두세요.',
  },
  /* 2026-10-19 Junha·사용자 «완전 삭제는 AE·KOL 요청 시 master 계정에 요청이 오게» — 대표가 아니면 예약 대신 요청, 대표가 승인 */
  {
    key: 'canRequestTrashPurge', route: '/trash', group: '메뉴', label: '휴지통 삭제 요청',
    where: '휴지통 화면 각 줄의 「삭제 요청」 버튼 (「휴지통」도 켜져 있어야 보입니다)',
    what: '대표가 아닌 사람이 완전 삭제를 요청합니다. 요청하면 대표 계정에 알림이 가고, 대표가 승인하면 30일 뒤 완전히 지워집니다. 승인·삭제 예약은 이 표와 상관없이 대표만 할 수 있습니다.',
  },
  {
    key: 'canPreviewRoles', group: '메뉴', label: '역할 미리보기',
    where: '왼쪽 사이드바 맨 아래 「역할 미리보기」 버튼',
    what: '다른 역할의 화면을 그대로 볼 수 있는 기능입니다. 우리 쪽 사람만 쓰는 도구라 클라이언트·배송사·크리에이터에게는 꺼두세요.',
    danger: true,
  },

  // 금액
  {
    key: 'canViewClientPrice', route: '/campaign', group: '금액', label: 'Client Price 컬럼',
    where: '캠페인 상세 → 인플루언서 / 결과 / 2차 활용 / 정산 탭의 표 컬럼',
    what: '클라이언트에게 청구하는 금액(Client Price, Client Price Final) 컬럼이 통째로 사라집니다.',
  },
  {
    key: 'canViewKolPrice', route: '/campaign', group: '금액', label: 'KOL 단가·원가 컬럼',
    where: '캠페인 상세 → 인플루언서 / 진행 / 2차 활용 / 정산 탭의 표 컬럼',
    what: '크리에이터에게 주는 단가·원가(KOL Rate, Expected Cost) 컬럼이 사라집니다. 섭외를 하려면 이 값이 필요해서 KOL Specialist 도 켜져 있습니다 — 대신 클라이언트 청구가(위 항목)는 꺼져 있어 마진은 드러나지 않습니다.',
  },
  {
    key: 'canCalculateClientPrice', route: '/campaign', group: '금액', label: 'Client 공급가 계산',
    where: '캠페인·My KOLs 화면의 2차 사이드바 「Client 공급가 계산」 버튼',
    what: '마크업을 얹어 공급가를 계산하는 도구. 마진 로직이 드러납니다.',
  },

  // 캠페인
  {
    key: 'canCreateCampaign', route: '/campaign', group: '캠페인', label: '캠페인 생성',
    where: '캠페인 화면 2차 사이드바 「+ 신규 캠페인」 버튼',
    what: '끄면 기존 캠페인은 보되 새로 만들 수 없습니다.',
  },
  {
    key: 'canDeleteCampaign', route: '/campaign', group: '캠페인', label: '캠페인 삭제',
    where: '캠페인 상세 → 캠페인 수정 모달 안의 [삭제] 버튼',
    what: '지우면 휴지통으로 옮겨지고 되살릴 수 있습니다. 완전 삭제는 휴지통에서 대표 승인을 받아야 합니다(2026-10-19 부터 KOL Specialist 도 기본 켜짐).',
    danger: true,
  },

  // My KOLs
  {
    key: 'canCreateMyKolsList', route: '/my-kols', group: 'My KOLs', label: '리스트 생성',
    where: 'My KOLs 화면 2차 사이드바 「신규 리스트」 버튼',
    what: '크리에이터 리스트를 새로 만드는 기능.',
  },
  {
    key: 'canEditMyKols', route: '/my-kols', group: 'My KOLs', label: '수정·삭제',
    where: 'My KOLs 표의 수정·삭제 버튼',
    what: '끄면 목록을 보기만 하고 고칠 수 없습니다.',
  },
  {
    key: 'canAssignMyKols', route: '/my-kols', group: 'My KOLs', label: '검색·CSV 배정',
    where: 'My KOLs 화면의 「검색 배정」·「CSV 배정」 버튼',
    what: '크리에이터를 캠페인에 붙이는 기능.',
  },

  // 배송
  {
    key: 'canCreateShipping', route: '/shipping', group: '배송', label: '신규 배송 등록',
    where: 'Shipping 화면 2차 사이드바 「신규 배송 등록」 버튼',
    what: '배송사는 등록이 아니라 송장 입력만 하므로 기본은 꺼져 있습니다.',
  },
  {
    key: 'canEstimateShipping', route: '/shipping', group: '배송', label: '예상 일정·비용 계산',
    where: 'Shipping 화면 2차 사이드바 「예상 일정 및 비용 계산하기」 버튼',
    what: '내부 원가 계산 도구입니다.',
  },
  {
    key: 'canDeleteShipping', route: '/shipping', group: '배송', label: '배송 삭제',
    where: '배송 상세 → 배송 정보 탭 맨 아래 「삭제하기」 버튼 (1차 등록 전까지만)',
    what: '지우면 휴지통으로 옮겨지고 되살릴 수 있습니다. 1차 등록이 끝난 배송은 대표만 지울 수 있고, 완전 삭제는 휴지통에서 대표 승인을 받아야 합니다.',
    danger: true,
  },
  {
    key: 'canEditShippingDetail', group: '배송', label: '배송 상세 수정·삭제',
    where: '배송 상세 화면 (아직 이 권한이 연결되지 않았습니다)',
    what: '지금은 켜고 꺼도 화면이 바뀌지 않습니다. 연결이 필요하면 알려주세요.',
    unused: true,
  },
  {
    key: 'canViewFullShippingDetail', group: '배송', label: '배송 상세 전체 보기',
    where: '배송 상세 화면 (아직 이 권한이 연결되지 않았습니다)',
    what: '지금은 켜고 꺼도 화면이 바뀌지 않습니다.',
    unused: true,
  },
  {
    key: 'canViewShippingMemo', group: '배송', label: '메모 섹션',
    where: '배송 상세 화면의 메모 (아직 이 권한이 연결되지 않았습니다)',
    what: '지금은 켜고 꺼도 화면이 바뀌지 않습니다.',
    unused: true,
  },
  {
    key: 'canViewAllNotes', group: '배송', label: 'Client Note 외 노트',
    where: '배송 상세 화면의 노트 (아직 이 권한이 연결되지 않았습니다)',
    what: '지금은 켜고 꺼도 화면이 바뀌지 않습니다.',
    unused: true,
  },
  {
    key: 'canSearchByClient', route: '/shipping', group: '배송', label: '클라이언트로 검색',
    where: '캠페인·Shipping 2차 사이드바의 클라이언트 검색창',
    what: '광고주 계정에게는 자기 것만 보이면 되므로 기본은 꺼져 있습니다.',
  },
];

/** 기능 설명 옆에 붙는 도움말 사진 경로. 파일이 없으면 화면에서 자동으로 숨긴다. */
export const helpImageSrc = (key) => `/assets/permission-help/${key}.png`;

/**
 * 기본값 — 2026-07-29 시점 코드 동작을 그대로 옮긴 것.
 * (M=MASTER, AE, KOL=KOL Specialist, C=CLIENT, D=DELIVERY, I=INFLUENCER)
 */
/**
 * 기본 권한 표.
 *
 * AE 는 지금 MASTER 와 같다(2026-08-12 대표님 결정 — 「Master, AE(Master와 동일),
 * KOL(Manager와 동일), Client」). 줄일 자리는 이 표에서 AE 열의 체크를 끄면 된다.
 * 먼저 볼 만한 세 줄은 회사·권한 관리 · 역할 미리보기 · 캠페인 삭제다 —
 * 특히 회사·권한 관리는 이 표 자체를 고치는 권한이라, 켜 두면 AE 가 자기 권한을 올릴 수 있다.
 */
export const DEFAULT_MATRIX = {
  //                        M      AE     KOL    C      D      I
  canViewDashboard:        { MASTER: true, AE: true, KOL: true, CLIENT: true, DELIVERY: false, INFLUENCER: false },
  canViewKolsDB:           { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canViewMyKols:           { MASTER: true, AE: true, KOL: true, CLIENT: true, DELIVERY: false, INFLUENCER: false },
  canViewCampaign:         { MASTER: true, AE: true, KOL: true, CLIENT: true, DELIVERY: false, INFLUENCER: false },
  canViewShipping:         { MASTER: true, AE: true, KOL: true, CLIENT: true, DELIVERY: true, INFLUENCER: false },
  canViewDelivery:         { MASTER: true, AE: true, KOL: true, CLIENT: true, DELIVERY: true, INFLUENCER: false },
  canViewInvoice:          { MASTER: true, AE: true, KOL: false, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canViewSettlement:       { MASTER: true, AE: true, KOL: false, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canManageClientInvites:  { MASTER: true, AE: true, KOL: false, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canViewTrash:            { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canRequestTrashPurge:    { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canPreviewRoles:         { MASTER: true, AE: true, KOL: false, CLIENT: false, DELIVERY: false, INFLUENCER: false },

  canViewClientPrice:      { MASTER: true, AE: true, KOL: false, CLIENT: true, DELIVERY: false, INFLUENCER: false },
  canViewKolPrice:         { MASTER: true, AE: true, KOL: true,  CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canCalculateClientPrice: { MASTER: true, AE: true, KOL: false, CLIENT: false, DELIVERY: false, INFLUENCER: false },

  canCreateCampaign:       { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canDeleteCampaign:       { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },

  canCreateMyKolsList:     { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canEditMyKols:           { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canAssignMyKols:         { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },

  canCreateShipping:       { MASTER: true, AE: true, KOL: true, CLIENT: true, DELIVERY: false, INFLUENCER: false },
  canEstimateShipping:     { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canDeleteShipping:       { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canEditShippingDetail:   { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canViewFullShippingDetail: { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: false, INFLUENCER: false },
  canViewShippingMemo:     { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: true, INFLUENCER: false },
  canViewAllNotes:         { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: true, INFLUENCER: false },
  canSearchByClient:       { MASTER: true, AE: true, KOL: true, CLIENT: false, DELIVERY: true, INFLUENCER: false },
};

/* 서버 값을 이 브라우저에 잠깐 들고 있는 칸 — 새로고침 직후 서버 응답이 오기 전에도 같은 화면을 그리려고.
   정본은 서버다. 예전 «이 브라우저에만 저장» 칸(v1)은 더 읽지 않고 지운다 — 남아 있으면 같은 PC 에
   다음에 로그인한 사람에게도 그 표가 적용됐다(2026-10-02 검토). */
const SERVER_CACHE_KEY = 'blbent.permissionMatrix.server.v1';
const LEGACY_LOCAL_KEY = 'blbent.permissionMatrix.v1';
const REFRESH_MIN_MS = 60 * 1000;

/** 기본값 위에 저장분을 덮는다 — 코드에 권한이 새로 생겨도 빠지지 않게. 모르는 역할·값은 버린다. */
const mergeWithDefault = (saved) => {
  const merged = {};
  Object.keys(DEFAULT_MATRIX).forEach((key) => {
    const row = { ...DEFAULT_MATRIX[key] };
    const s = saved && typeof saved === 'object' ? saved[key] : null;
    if (s && typeof s === 'object') {
      MATRIX_ROLES.forEach(({ key: role }) => {
        if (typeof s[role] === 'boolean') row[role] = s[role];
      });
    }
    merged[key] = row;
  });
  return merged;
};

const readCache = () => {
  try {
    localStorage.removeItem(LEGACY_LOCAL_KEY);
    const raw = localStorage.getItem(SERVER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

let current = mergeWithDefault(readCache());
const listeners = new Set();

/** 지금 적용 중인 권한 표(서버 값을 얹은 것). 값이 바뀔 때만 새 객체가 된다. */
export const loadMatrix = () => current;

/** 권한 표가 바뀌면 부른다 — usePermission 이 이걸로 화면을 다시 그린다(새로고침 없이). */
export const subscribeMatrix = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** 서버에서 받은 값을 적용한다. null 이면 기본값. */
export const applyServerMatrix = (value) => {
  current = mergeWithDefault(value);
  try {
    if (value) localStorage.setItem(SERVER_CACHE_KEY, JSON.stringify(value));
    else localStorage.removeItem(SERVER_CACHE_KEY);
  } catch { /* 저장 공간이 막혀도 이번 화면에는 적용된다 */ }
  listeners.forEach((fn) => fn());
};

let lastFetchAt = 0;
let inflight = null;

/**
 * 서버의 권한 표를 다시 읽는다. 로그인 전이면 안 읽는다. 1분 안에 다시 부르면 건너뛴다(force 면 바로).
 * 못 읽으면(옛 서버·네트워크) 지금 값을 그대로 쓴다 — 실패했다고 권한을 넓히거나 좁히지 않는다.
 */
export const refreshMatrixFromServer = ({ force = false } = {}) => {
  if (!hasUsableSession()) return Promise.resolve(current);
  if (inflight) return inflight;
  if (!force && Date.now() - lastFetchAt < REFRESH_MIN_MS) return Promise.resolve(current);
  lastFetchAt = Date.now();
  inflight = Promise.resolve()
    .then(() => getPermissionMatrix())
    .then((value) => { applyServerMatrix(value); return current; })
    .catch(() => current)
    .finally(() => { inflight = null; });
  return inflight;
};

/** 서버에 저장(MASTER 만 — 서버가 다른 계정은 403). 성공하면 이 화면에도 바로 적용된다. */
export const saveMatrix = async (matrix) => {
  const value = mergeWithDefault(matrix);
  const saved = await savePermissionMatrix(value);
  lastFetchAt = Date.now();
  applyServerMatrix(saved || value);
  return current;
};

/* 다른 창·탭에서 돌아오면 다시 읽는다 — 대표님이 표를 바꾼 뒤 다른 사람이 창으로 돌아오는 순간 반영된다. */
if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => { refreshMatrixFromServer(); });
}

/** 시험 전용 — 모듈 상태를 처음으로 되돌린다. */
export const __resetMatrixStoreForTest = () => {
  current = mergeWithDefault(readCache());
  lastFetchAt = 0;
  inflight = null;
};

/** 기본값과 다른 칸만 뽑는다 — 화면 표시·개발자 전달용. */
export const diffFromDefault = (matrix) => {
  const diff = [];
  PERMISSION_DEFS.forEach(({ key, label }) => {
    MATRIX_ROLES.forEach(({ key: role, label: roleLabel }) => {
      const now = matrix?.[key]?.[role];
      const base = DEFAULT_MATRIX[key]?.[role];
      if (now !== base) diff.push({ key, label, role, roleLabel, from: base, to: now });
    });
  });
  return diff;
};

/**
 * serviceType(+adminRole)을 권한 표의 열 이름으로 바꾼다.
 * adminRole 이 없는 MANAGER 는 KOL Specialist 으로 본다(금액이 새는 쪽보다 안전).
 */
export const toMatrixRole = (serviceType, adminRole) => {
  switch (serviceType) {
    case 'MASTER':
      return 'MASTER';
    case 'MANAGER':
      return adminRole === 'AE' ? 'AE' : 'KOL';
    case 'CLIENT':
      return 'CLIENT';
    case 'DELIVERY_THAILAND':
    case 'DELIVERY_VIETNAM':
      return 'DELIVERY';
    case 'INFLUENCER':
      return 'INFLUENCER';
    default:
      return '';
  }
};
